import { type BrandName } from "@elit-hair/brands";

import {
    CONVERTED_SALES_CALL_RESULTS,
    PENDING_SALES_CALL_RESULT,
    getSalesCallResultOptions,
} from "@/pages/appointments/constants/sales-call-result";
import type { Booking } from "@/queries/booking.queries";
import type { Team, TeamMember } from "@/types/team.types";

import type {
    AgentTimelineRow,
    AppointmentAnalyticsSummary,
    AppointmentStatusCount,
} from "../types/appointment-analytics.types";

export const APPOINTMENT_ANALYTICS_DAY_START_HOUR = 0;
export const APPOINTMENT_ANALYTICS_DAY_END_HOUR = 24;

export const getTeamMemberUserId = (member: TeamMember): string => member.user?.id ?? member.id;

export const getTeamMemberLabel = (member: TeamMember): string =>
    member.user?.name ?? member.user?.email ?? getTeamMemberUserId(member);

export const getTeamMemberIds = (team: Team): string[] =>
    team.members.map(getTeamMemberUserId).filter((id, index, ids) => ids.indexOf(id) === index);

export const buildAppointmentAnalyticsSummary = (
    appointments: Booking[],
    brandName?: BrandName
): AppointmentAnalyticsSummary => {
    const options = getSalesCallResultOptions(brandName);
    const counts = new Map<string, number>();

    appointments.forEach((appointment) => {
        const key = appointment.result || PENDING_SALES_CALL_RESULT;
        counts.set(key, (counts.get(key) ?? 0) + 1);
    });

    const statusCounts: AppointmentStatusCount[] = [
        {
            value: PENDING_SALES_CALL_RESULT,
            labelKey: "appointment_status_pending",
            colorClass: "bg-muted-foreground/50",
            count: counts.get(PENDING_SALES_CALL_RESULT) ?? 0,
        },
        ...options.map((option) => ({
            value: option.value,
            labelKey: option.labelKey,
            colorClass: option.colorClass,
            count: counts.get(option.value) ?? 0,
        })),
    ];

    const converted = appointments.filter(
        (appointment) => appointment.result && CONVERTED_SALES_CALL_RESULTS.has(appointment.result)
    ).length;
    const total = appointments.length;

    return {
        total,
        converted,
        conversionRate: total > 0 ? (converted / total) * 100 : 0,
        statusCounts,
    };
};

export const buildAgentTimelineRows = (
    team: Team | null,
    assignedAppointments: Booking[],
    unassignedAppointments: Booking[]
): AgentTimelineRow[] => {
    const appointmentsByHost = new Map<string, Booking[]>();

    assignedAppointments.forEach((appointment) => {
        if (!appointment.hostId) {
            return;
        }

        const existing = appointmentsByHost.get(appointment.hostId) ?? [];
        existing.push(appointment);
        appointmentsByHost.set(appointment.hostId, existing);
    });

    const agentRows: AgentTimelineRow[] = (team?.members ?? []).map((member) => {
        const memberId = getTeamMemberUserId(member);
        const appointments = [...(appointmentsByHost.get(memberId) ?? [])].sort((a, b) =>
            a.startTime.localeCompare(b.startTime)
        );

        return {
            key: memberId,
            label: getTeamMemberLabel(member),
            isUnassigned: false,
            appointments,
            member,
        };
    });

    return [
        ...agentRows,
        {
            key: "unassigned",
            label: "",
            isUnassigned: true,
            appointments: [...unassignedAppointments].sort((a, b) => a.startTime.localeCompare(b.startTime)),
        },
    ];
};
